import { api } from './api';

// API Response wrapper type: { success, code, message, data }
interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

// Logged-in user's personnel profile (populated) returned by GET /api/v1/users/me
export interface MyProfile {
  _id?: string;
  userId?: string;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  phoneNo?: string;
  gender?: string;
  title?: string;
  isOnDuty?: boolean;
  ministryId?: string | null;
  unit?: {
    _id?: string;
    name?: string;
    unitCode?: string;
  } | null;
  department?: {
    _id?: string;
    name?: string;
  } | null;
  designation?: {
    _id?: string;
    name?: string;
  } | null;
  createdAt?: string;
  updatedAt?: string;
  /** True when the account has no personnel record behind it. */
  isStandaloneAccount?: boolean;
  /** The caller's own login account — see MyAccount. */
  account?: MyAccount | null;
  [key: string]: unknown;
}

/**
 * The login account behind the profile (user_master), reduced by the backend to
 * what the owner may see about themselves. Secrets are never members of it.
 */
export interface MyAccount {
  _id?: string;
  userId?: string;
  lastLoginDateTime?: string | null;
  isRegistered?: boolean;
  isVerified?: boolean;
  isLoggedIn?: boolean;
  userStatus?: string | null;
  blocked?: boolean;
  reasonForBlock?: string | null;
  failedLoginAttemptCounter?: number;
  contact?: { email?: string; phoneNo?: string } | null;
  contactSource?: string;
  createdAt?: string;
  updatedAt?: string | null;
  /** Sign-ins that have not been revoked or expired, newest use first. */
  sessions?: MySession[];
}

/** One live sign-in: a refresh token that is still good. */
export interface MySession {
  _id: string;
  ipAddress?: string | null;
  startedAt?: string | null;
  lastUsedAt?: string | null;
  expiresAt?: string | null;
}

// A role that grants the user access within a unit (from GET /users/me/units)
export interface MyUnitRole {
  roleId: string;
  roleKey?: string;
  roleName?: string;
  roleBindingId: string;
  inheritChildren?: boolean;
}

// A unit the logged-in user holds active role bindings in (GET /users/me/units)
export interface MyUnit {
  unitId: string | null;   // null = global grant (applies everywhere)
  unitName?: string | null;
  isHomeUnit?: boolean;
  isGlobal?: boolean;
  roles: MyUnitRole[];
}

// Response of POST /users/me/switch-unit
export interface SwitchUnitResult {
  accessToken: string;
  tokenType?: string;
  expiresIn?: number;
  unitId: string;
  unitName?: string;
}

// A selectable role (one per active assignment) from GET /users/me/roles
export interface MyRole {
  roleBindingId: string;
  roleId: string;
  roleKey?: string;
  roleName?: string;
  unitId: string | null;
  unitName?: string | null;
  isGlobal?: boolean;
  inheritChildren?: boolean;
  description?: string | null;
}

// Response of POST /users/me/select-role
export interface SelectRoleResult {
  accessToken: string;
  tokenType?: string;
  expiresIn?: number;
  roleBindingId: string;
  roleId?: string;
  roleName?: string;
  unitId?: string | null;
  unitName?: string | null;
}

export const profileService = {
  // Get the logged-in user's personnel profile
  getMe: async (): Promise<MyProfile> => {
    const response = await api.get<ApiResponse<MyProfile> | MyProfile>('/api/v1/users/me');
    // Handle both wrapped and unwrapped response formats
    const data = (response.data as ApiResponse<MyProfile>)?.data ?? response.data;
    return data as MyProfile;
  },

  // Get the logged-in user's resolved permission keys
  getMyPermissions: async (): Promise<string[]> => {
    const response = await api.get<any>('/api/v1/users/me/permissions');
    const data = response.data?.data ?? response.data;
    // BE returns { permissions: [{ key, scope, displayName }], total } (envelope-unwrapped).
    const list = Array.isArray(data) ? data : (data?.permissions ?? []);
    return list
      .map((perm: any) => (typeof perm === 'string' ? perm : perm?.key))
      .filter(Boolean);
  },

  // Get the units the logged-in user has active role bindings in (for the unit switcher)
  getMyUnits: async (): Promise<MyUnit[]> => {
    const response = await api.get<any>('/api/v1/users/me/units');
    const data = response.data?.data ?? response.data;
    // BE returns { units: [...], total } (envelope-unwrapped).
    const list = Array.isArray(data) ? data : (data?.units ?? []);
    return list as MyUnit[];
  },

  // Re-issue an access token scoped to a specific unit. Caller must persist the
  // returned accessToken (then re-resolve permissions) to act within that unit.
  switchUnit: async (unitId: string): Promise<SwitchUnitResult> => {
    const response = await api.post<any>('/api/v1/users/me/switch-unit', { unitId });
    const data = response.data?.data ?? response.data;
    return data as SwitchUnitResult;
  },

  // List the roles the user can log in as (one per active assignment), each with its unit.
  getMyRoles: async (): Promise<MyRole[]> => {
    const response = await api.get<any>('/api/v1/users/me/roles');
    const data = response.data?.data ?? response.data;
    const list = Array.isArray(data) ? data : (data?.roles ?? []);
    return list as MyRole[];
  },

  // Pick a role → get a NEW access token scoped to that single role + its unit.
  // Caller must persist the returned accessToken (then reload / re-resolve permissions).
  selectRole: async (roleBindingId: string): Promise<SelectRoleResult> => {
    const response = await api.post<any>('/api/v1/users/me/select-role', { roleBindingId });
    const data = response.data?.data ?? response.data;
    return data as SelectRoleResult;
  },
};
